import orderBy from "lodash/orderBy";
import PropTypes from "prop-types";
import React from "react";
import { Link, Redirect } from "react-router-dom";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { Table, Column } from "ui/table";
import { getErrorMessage } from "util/response";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

import { ReportFilter } from "./report-filter";

class UptimeReportDevicesPage extends React.PureComponent {
  static get propTypes() {
    return {
      date: PropTypes.string.isRequired,
      devices: PropTypes.object.isRequired,
      end: PropTypes.string.isRequired,
      fetchDevicesUptimeReport: PropTypes.func.isRequired,
      history: PropTypes.object.isRequired,
      network: PropTypes.object,
      networkOptions: PropTypes.array.isRequired,
      report: PropTypes.object.isRequired,
      start: PropTypes.string.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      unloadDevicesUptimeReport: PropTypes.func.isRequired,
    };
  }

  constructor() {
    super();

    this.state = {
      errorMessage: "",
      submitted: false,
    };

    this.tableOptions = {
      sortName: "id",
      sortOrder: "desc",
    };
    this.uptimeExportHeader = [
      ["Device ID", "Name", "Uptime", "Current Network"],
    ];

    this._onReportRun = this._onReportRun.bind(this);
    this._formatDeviceLink = this._formatDeviceLink.bind(this);
  }

  componentDidMount() {
    this._fetchReport(this.props);
  }

  componentDidUpdate(prevProps) {
    const { network, date } = prevProps;
    if (network !== this.props.network || date !== this.props.date) {
      this._fetchReport(this.props);
    }
  }

  componentWillUnmount() {
    this.props.unloadDevicesUptimeReport();
  }

  _doFetchReport(props) {
    const { date, fetchDevicesUptimeReport, network } = props;
    const serviceArgs = {
      date,
      networkId: network.id,
    };
    return fetchDevicesUptimeReport(serviceArgs)
      .catch((e) => {
        this.setState({ errorMessage: getErrorMessage(e) });
      })
      .finally(() => {
        this.setState({ submitted: false });
      });
  }

  _fetchReport(props) {
    const { network, date } = props;
    if (network && date) {
      const doFetchReport = this._doFetchReport.bind(this, props);
      this.setState({ submitted: true }, doFetchReport);
    }
  }

  _onReportRun(networkId, start, end) {
    this.props.history.push(`/reports/uptime/${networkId}/${start}/${end}`);
  }

  _exportFileName(prefix) {
    const { network, date } = this.props;
    const name = network.name;
    return `${prefix}_${name}_${date}.csv`;
  }

  _exportUptimeData(data) {
    const { sortName, sortOrder } = this.tableOptions;
    const uptimeData = orderBy(data, sortName, sortOrder).map((row) => {
      return [row.deviceId, row.name, row.uptime, row.networkName];
    });

    return [...this.uptimeExportHeader, ...uptimeData];
  }

  _formatDeviceLink(row, device) {
    const { network, start, end } = this.props;
    if (!network) {
      return device.deviceId;
    }
    const url = `/reports/uptime/${network.id}/${start}/${end}/device/${device.id}`;
    return <Link to={url}>{device.deviceId}</Link>;
  }

  _formatNetwork(networkName, row) {
    const url = `/networks/${row.networkId}`;
    return <Link to={url}>{networkName}</Link>;
  }

  _formatUptime(uptime) {
    return `${uptime.toFixed(2)}%`;
  }

  _renderInfo() {
    const { network, date } = this.props;
    if (!network) {
      return null;
    }

    const text = `These devices were part of the
      ${network.name} on ${date}`;
    return (
      <div className="uptime-devices-info">
        <i className="fa fa-info-circle" />
        <span>{text}</span>
      </div>
    );
  }

  _renderDeviceUptimeReport() {
    const { devices, report } = this.props;

    const data = Object.keys(report)
      .filter((id) => devices[id])
      .map((id) => {
        const device = devices[id];
        const uptime = report[id];
        return {
          uptime,
          deviceId: device.device_id,
          id: device.id,
          name: device.name,
          networkId: device.network_id,
          networkName: device.network_name,
        };
      });

    if (data.length === 0) {
      return <p>No data to display.</p>;
    }
    const _export = this._exportUptimeData.bind(this, data);

    return (
      <Table
        data={data}
        hover={true}
        pagination={true}
        search={true}
        options={this.tableOptions}
        exportFile={this._exportFileName("network-uptime-date")}
        exportFunc={_export}
      >
        <Column
          key="id"
          dataField="id"
          dataSort={false}
          isKey={true}
          hidden={true}
        >
          ID
        </Column>
        <Column
          key="deviceId"
          dataField="deviceId"
          dataSort={true}
          dataFormat={this._formatDeviceLink}
        >
          Device ID
        </Column>
        <Column key="name" dataField="name" dataSort={true}>
          Name
        </Column>
        <Column
          key="uptime"
          dataField="uptime"
          dataSort={true}
          dataFormat={this._formatUptime}
        >
          Uptime
        </Column>
        <Column
          key="networkName"
          dataField="networkName"
          dataSort={true}
          dataFormat={this._formatNetwork}
        >
          Current Network
        </Column>
      </Table>
    );
  }

  _renderView() {
    const { date, network, networkOptions, start, end, storesLoaded } =
      this.props;
    const { errorMessage, submitted } = this.state;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    if (!network) {
      return <Redirect to="/404" />;
    }

    const title = `Network Uptime: ${network.name} ${date}`;

    return (
      <div>
        <Notification error={errorMessage} />
        <ReportFilter
          network={network}
          options={networkOptions}
          start={start}
          end={end}
          onRun={this._onReportRun}
          submitted={submitted}
        />
        <Box title={title}>
          {this._renderDeviceUptimeReport()}
          {this._renderInfo()}
        </Box>
      </div>
    );
  }

  render() {
    const view = this._renderView();

    return <Page title="Uptime Report">{view}</Page>;
  }
}

export { UptimeReportDevicesPage };
